import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { WhatsAppInboxProvider } from './contexts/WhatsAppInboxContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import OnboardingGuard from './components/guards/OnboardingGuard';
import SubscriptionGuard from './components/subscription/SubscriptionGuard';
import ThemeContextProvider from './contexts/ThemeContext';
import { usePageTracking } from './hooks/usePageTracking';
import usageTrackingService from './services/usage/usageTrackingService';
import { soundNotificationService, NotificationSoundType } from './services/notifications/soundNotificationService';
import { startFinancialEventsListener } from './services/financial/core/financialEventsListener';

function PermissionEditAlias() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/permissions/edit/${id}`} replace />;
}

/** Catch-all: preserva a rota pedida para o login redirecionar após auth */
function CatchAllRedirect() {
  const location = useLocation();
  return <Navigate to="/login" replace state={{ from: location }} />;
}

/** Rotas CRM legadas → Gestão de Clientes / Relatório de Clientes (funil) */
function CrmLegacyRedirect() {
  const location = useLocation();
  const to =
    location.pathname.includes('/funnel') || location.pathname.includes('/leads')
      ? '/reports/clients'
      : '/clients';
  return <Navigate to={to} replace />;
}

/** Alias legado `/checkin/:clinicId` → check-in clínico canônico */
function CheckInPathAlias() {
  const { clinicId } = useParams<{ clinicId: string }>();
  return <Navigate to={`/check-in/${clinicId}`} replace />;
}

/** Deep links de notificação → Laboratório com query */
function LabDeepLinkRedirect({ kind }: { kind: 'orders' | 'results' }) {
  const { id } = useParams<{ id: string }>();
  const tab = kind === 'orders' ? 'orders' : 'results';
  const key = kind === 'orders' ? 'orderId' : 'resultId';
  return <Navigate to={`/laboratory?tab=${tab}&${key}=${id || ''}`} replace />;
}

// Lazy loading dos componentes para melhor performance
const LoginPage = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Patients = lazy(() => import('./pages/Patients'));
const PatientDashboardPage = lazy(() => import('./pages/PatientDashboardPage'));
const PatientChartHubPage = lazy(() => import('./pages/PatientChartHubPage'));
const LaboratoryPage = lazy(() => import('./pages/LaboratoryPage'));
const PharmacyPage = lazy(() => import('./components/pharmacy/UnifiedPharmacyPage'));
const InjectablesHubPage = lazy(() => import('./pages/InjectablesHubPage'));
const InjectableSessionsPage = lazy(() => import('./pages/InjectableSessionsPage'));
const InjectableTreatmentPlansPage = lazy(() => import('./pages/InjectableTreatmentPlansPage'));
const InjectableSessionPage = lazy(() => import('./pages/InjectableSessionPage'));
const InjectableClinicalReportPage = lazy(() => import('./pages/InjectableClinicalReportPage'));
const InjectablePatientPortalPage = lazy(() => import('./pages/InjectablePatientPortalPage'));
const Appointments = lazy(() => import('./pages/Appointments'));
const SalesHubPage = lazy(() => import('./pages/SalesHubPage'));
const ProductsHubPage = lazy(() => import('./pages/ProductsHubPage'));
const ClientsHubPage = lazy(() => import('./pages/ClientsHubPage'));
const CheckInAdminPage = lazy(() => import('./pages/CheckInAdminPage'));
const WaitlistPage = lazy(() => import('./pages/WaitlistPage'));
const ClientTasksPage = lazy(() => import('./pages/ClientTasksPage'));
const BookingGridsPage = lazy(() => import('./pages/BookingGridsPage'));
const ClientInterestsPage = lazy(() => import('./pages/ClientInterestsPage'));
const ClientTagsPage = lazy(() => import('./pages/ClientTagsPage'));
const PriceTablePage = lazy(() => import('./pages/PriceTablePage'));
const ServiceRoomsPage = lazy(() => import('./pages/ServiceRoomsPage'));
const CertificateTemplatesPage = lazy(() => import('./pages/CertificateTemplatesPage'));
const ContractTemplatesPage = lazy(() => import('./pages/ContractTemplatesPage'));
const SuppliersPage = lazy(() => import('./pages/SuppliersPage'));
const CitiesPage = lazy(() => import('./pages/CitiesPage'));
const HolidaysPage = lazy(() => import('./pages/HolidaysPage'));
const ClinicConveniosPage = lazy(() => import('./pages/ClinicConveniosPage'));
const PaymentMethodsPage = lazy(() => import('./pages/PaymentMethodsPage'));
const CostCentersPage = lazy(() => import('./pages/CostCentersPage'));
const EvolutionPatternsPage = lazy(() => import('./pages/EvolutionPatternsPage'));
const DevicesPage = lazy(() => import('./pages/DevicesPage'));
const ExercisesPage = lazy(() => import('./pages/ExercisesPage'));
const MuscleGroupsPage = lazy(() => import('./pages/MuscleGroupsPage'));
const AssessmentFormsPage = lazy(() => import('./pages/AssessmentFormsPage'));
const AssessmentFormEditorPage = lazy(() => import('./pages/AssessmentFormEditorPage'));
const ClickableRecordsPage = lazy(() => import('./pages/ClickableRecordsPage'));
const ClickableRecordEditorPage = lazy(() => import('./pages/ClickableRecordEditorPage'));
const ClinicalFlowPage = lazy(() => import('./pages/ClinicalFlowPage'));
const WellhubInboxPage = lazy(() => import('./pages/WellhubInboxPage'));
const CheckInPage = lazy(() => import('./pages/CheckInPage'));
const ClinicTvBoardPage = lazy(() => import('./pages/ClinicTvBoardPage'));
const ConveniosPage = lazy(() => import('./pages/ConveniosPage'));
const Professionals = lazy(() => import('./pages/Professionals'));
const ProfessionalsHomeCarePage = lazy(() => import('./pages/ProfessionalsHomeCarePage'));
const Inventory = lazy(() => import('./pages/Inventory'));
const NewFinancialPage = lazy(() => import('./pages/financial/NewFinancialPage'));
const TransactionsPage = lazy(() => import('./pages/financial/TransactionsPage'));
const AccountsReceivablePage = lazy(() => import('./pages/financial/AccountsReceivablePage'));
const PaymentsPage = lazy(() => import('./pages/financial/PaymentsPage'));
const SettleReceiptsPage = lazy(() => import('./pages/financial/SettleReceiptsPage'));
const ReceiptIssuancePage = lazy(() => import('./pages/financial/ReceiptIssuancePage'));
const CommissionPaymentPage = lazy(() => import('./pages/financial/CommissionPaymentPage'));
const GeneralSalesPage = lazy(() => import('./pages/financial/GeneralSalesPage'));
const CashFlowPage = lazy(() => import('./pages/financial/CashFlowPage'));
const DREManagerialPage = lazy(() => import('./pages/financial/DREManagerialPage'));
const FinancialStatementPage = lazy(() => import('./pages/financial/FinancialStatementPage'));
const DailyCashPage = lazy(() => import('./pages/financial/DailyCashPage'));
const FixedRevenueSimulatorPage = lazy(() => import('./pages/financial/FixedRevenueSimulatorPage'));
const FinancialReportsPage = lazy(() => import('./pages/financial/ReportsPage'));
const ReconciliationPage = lazy(() => import('./pages/financial/ReconciliationPage'));
const OperationalDashboardPage = lazy(() => import('./pages/financial/OperationalDashboardPage'));
const AnalyticsDashboardPage = lazy(() => import('./pages/financial/AnalyticsDashboardPage'));
const BillingDashboard = lazy(() => import('./pages/financial/billing/BillingDashboard'));
const BillingPlansPage = lazy(() => import('./pages/financial/billing/BillingPlansPage'));
const BillingSubscriptionsPage = lazy(() => import('./pages/financial/billing/BillingSubscriptionsPage'));
const BillingAnalyticsPage = lazy(() => import('./pages/financial/billing/BillingAnalyticsPage'));
const BillingAlertsPage = lazy(() => import('./pages/financial/billing/BillingAlertsPage'));
const NotificationTemplatesPage = lazy(() => import('./pages/financial/billing/NotificationTemplatesPage'));
const StripeConnectSuccessPage = lazy(() => import('./pages/financial/billing/StripeConnectSuccessPage'));
const StripeConnectErrorPage = lazy(() => import('./pages/financial/billing/StripeConnectErrorPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const FinancialReportsHubPage = lazy(() => import('./pages/reports/FinancialReportsHubPage'));
const CommissionReportPage = lazy(() => import('./pages/reports/CommissionReportPage'));
const BillingHistoryReportPage = lazy(() => import('./pages/reports/BillingHistoryReportPage'));
const ReceivablesReportPage = lazy(() => import('./pages/reports/ReceivablesReportPage'));
const PayablesReportPage = lazy(() => import('./pages/reports/PayablesReportPage'));
const CostCentersReportPage = lazy(() => import('./pages/reports/CostCentersReportPage'));
const ReceiptsReportPage = lazy(() => import('./pages/reports/ReceiptsReportPage'));
const BirthdaysReportPage = lazy(() => import('./pages/reports/BirthdaysReportPage'));
const ClientsReportPage = lazy(() => import('./pages/reports/ClientsReportPage'));
const ManagerialAnalysisPage = lazy(() => import('./pages/reports/ManagerialAnalysisPage'));
const AppointmentsReportPage = lazy(() => import('./pages/reports/AppointmentsReportPage'));
const ProfessionalCommissioningReportPage = lazy(() => import('./pages/reports/ProfessionalCommissioningReportPage'));
const ProductSalesReportPage = lazy(() => import('./pages/reports/ProductSalesReportPage'));
const ContractsReportPage = lazy(() => import('./pages/reports/ContractsReportPage'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const ClientCenterPage = lazy(() => import('./pages/ClientCenterPage'));
const SystemDefinitionsPage = lazy(() => import('./pages/SystemDefinitionsPage'));
const SystemUsersPage = lazy(() => import('./pages/SystemUsersPage'));
const SystemUserFormPage = lazy(() => import('./pages/SystemUserFormPage'));
const PermissionRolesPage = lazy(() => import('./pages/PermissionRolesPage'));
const PermissionRoleEditPage = lazy(() => import('./pages/PermissionRoleEditPage'));
const OnlineBookingSalesPage = lazy(() => import('./pages/OnlineBookingSalesPage'));
const TelemedicinePage = lazy(() => import('./pages/TelemedicinePage'));
const TherapeuticAttendancePage = lazy(() => import('./pages/TherapeuticAttendancePage'));
const RemindersPage = lazy(() => import('./pages/RemindersPage'));
const PatientEHRPage = lazy(() => import('./pages/PatientEHRPage'));
const DynamicEHRPage = lazy(() => import('./pages/DynamicEHRPage'));
const PatientAIPage = lazy(() => import('./pages/PatientAIPage'));
const AIPage = lazy(() => import('./pages/AIPage'));
const IntegratedMedicalAIPage = lazy(() => import('./pages/IntegratedMedicalAIPage'));
const BeforeAfterAnalysisPage = lazy(() => import('./pages/BeforeAfterAnalysisPage'));
const ClinicalToolsPage = lazy(() => import('./pages/ClinicalToolsPage'));
const GoogleCalendarCallback = lazy(() => import('./pages/GoogleCalendarCallback'));
const SoftRedirectUserManagement = lazy(() => import('./pages/SoftRedirectUserManagement'));
const AdminUsageAnalyticsPage = lazy(() => import('./pages/AdminUsageAnalyticsPage'));
const MigrateInventoryPage = lazy(() => import('./pages/MigrateInventoryPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const SuccessPage = lazy(() => import('./pages/SuccessPage'));
const CancelPage = lazy(() => import('./pages/CancelPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const DesktopInstallersPage = lazy(() => import('./pages/DesktopInstallersPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const DataRetentionPolicyPage = lazy(() => import('./pages/DataRetentionPolicyPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const OnboardingPage = lazy(() => import('./components/onboarding/OnboardingPage'));

// Novas páginas do rodapé
const ProductFeaturesPage = lazy(() => import('./pages/features/ProductFeaturesPage'));
const SupportCenterPage = lazy(() => import('./pages/support/SupportCenterPage'));
const AboutCompanyPage = lazy(() => import('./pages/company/AboutCompanyPage'));

// Portal Público de Pacientes
const PatientPortal = lazy(() => import('./components/patient-portal/PatientPortal'));
const PatientPortalSuccess = lazy(() => import('./pages/PatientPortalSuccess'));
const ProfessionalSchedulePortal = lazy(() => import('./components/patient-portal/ProfessionalSchedulePortal'));
const ShortLinkRedirect = lazy(() => import('./components/patient-portal/ShortLinkRedirect'));
const PatientAccessPage = lazy(() => import('./pages/PatientAccessPage'));
const PublicClinicRegisterPage = lazy(() => import('./pages/PublicClinicRegisterPage'));

// Componentes de IA e Análise Clínica
const CriticalFindingsAlert = lazy(() => import('./components/clinical-evolution/CriticalFindingsAlert'));

// Sistema Inteligente de Análise

// Sistema Omnichannel (simplificado — Fase 1)
const OmnichannelChannels = lazy(() => import('./omnichannel/pages/ChannelsPage'));
const AutomaticMessagesPage = lazy(() => import('./omnichannel/pages/AutomaticMessagesPage'));
const SecretariaInboxPage = lazy(() => import('./omnichannel/inbox/SecretariaInboxPage'));
const OmnichannelRouteRedirect = lazy(() => import('./omnichannel/components/OmnichannelRouteRedirect'));
const OmnichannelSoftLandingPage = lazy(() => import('./omnichannel/pages/OmnichannelSoftLandingPage'));

const AppContent: React.FC = () => {
  const { currentUser } = useAuthContext();
  
  // Configurar listener para mensagens do Service Worker (sons de notificação)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'PLAY_NOTIFICATION_SOUND') {
          const soundType: NotificationSoundType = event.data.soundType || 'default';
          soundNotificationService.playSound(soundType).catch(error => {
            console.warn('⚠️ [APP] Erro ao tocar som do service worker:', error);
          });
        }
      });
    }
  }, []);

  // Inicializar tracking de erros quando usuário fizer login
  useEffect(() => {
    if (currentUser) {
      usageTrackingService.initializeErrorTracking(currentUser.uid);
    } else {
      usageTrackingService.reset();
    }
  }, [currentUser]);

  // Inicializar listener de eventos financeiros quando usuário fizer login
  useEffect(() => {
    if (currentUser?.clinicId) {
      const stopListener = startFinancialEventsListener(currentUser.clinicId);
      return () => {
        stopListener();
      };
    }
  }, [currentUser?.clinicId]);
  
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <WhatsAppInboxProvider>
        <AppContentInner />
      </WhatsAppInboxProvider>
    </Router>
  );
};

// Componente interno que fica DENTRO do Router
// Isso permite que usePageTracking() use useLocation() corretamente
const AppContentInner: React.FC = () => {
  const navigate = useNavigate();
  
  // Inicializar tracking de páginas - agora dentro do Router
  usePageTracking();

  // Adicionar listener para navegação do menu Electron
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).electron) {
      const electron = (window as any).electron;
      
      if (electron.onNavigate) {
        console.log('✅ [ELECTRON-MENU] Listener de navegação registrado');
        electron.onNavigate((path: string) => {
          console.log('🧭 [ELECTRON-MENU] Navegando para:', path);
          navigate(path);
        });
      } else {
        console.warn('⚠️ [ELECTRON-MENU] electron.onNavigate não disponível');
      }
    }
  }, [navigate]);
  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif'
      }}
    >
      <Suspense fallback={
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="100vh"
          sx={{ backgroundColor: 'background.default' }}
        >
          <CircularProgress 
            size={60} 
            thickness={4}
            sx={{ color: 'primary.main' }}
          />
        </Box>
      }>
        <Routes>
                {/* Rotas públicas */}
                <Route path="/" element={<LoginPage />} />
                {/* Variantes antigas de landing (Etapa 6): o site público é separado; links antigos levam ao login. */}
                <Route path="/landing/*" element={<Navigate to="/" replace />} />
                <Route path="/home" element={<Navigate to="/" replace />} />
                <Route path="/conversion" element={<Navigate to="/" replace />} />
                <Route path="/lp" element={<Navigate to="/" replace />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/checkout-modern" element={<Navigate to="/checkout" replace />} />
                <Route path="/success" element={<SuccessPage />} />
                <Route path="/cancel" element={<CancelPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/security" element={<SecurityPage />} />
                <Route path="/data-retention-policy" element={<DataRetentionPolicyPage />} />
                <Route path="/onboarding" element={
                  <ProtectedRoute>
                    <OnboardingPage />
                  </ProtectedRoute>
                } />
                
                {/* Páginas do rodapé */}
                <Route path="/features" element={<ProductFeaturesPage />} />
                <Route path="/support" element={<SupportCenterPage />} />
                <Route path="/company" element={<AboutCompanyPage />} />

                {/* Portal Público de Pacientes */}
                <Route path="/patient-portal/:clinicId" element={<PatientPortal />} />
                <Route path="/patient-portal/:clinicId/professional/:professionalId" element={<ProfessionalSchedulePortal />} />
                <Route path="/patient-portal/success/:appointmentId?" element={<PatientPortalSuccess />} />
                <Route path="/s/:shortCode" element={<ShortLinkRedirect />} />
                <Route path="/public/clinic-register/:clinicId" element={<PublicClinicRegisterPage />} />

                {/* Checkout Clínico — check-in QR e painel TV (público) */}
                <Route path="/check-in/:clinicId" element={<CheckInPage />} />
                {/* Alias legado /checkin → /check-in (não apaga; redireciona) */}
                <Route path="/checkin/:clinicId" element={<CheckInPathAlias />} />
                <Route path="/checkin/:clinicId/:patientId" element={<CheckInPathAlias />} />
                <Route path="/tv/:clinicId" element={<ClinicTvBoardPage />} />
                
                {/* Acesso do Paciente à Telemedicina */}
                <Route path="/telemedicine/join/:accessToken" element={<PatientAccessPage />} />
                
                {/* Link Livre para Agendamento */}
                
                {/* Novos portais */}
                
                {/* Rotas de teste temporárias */}

                {/* Rotas protegidas */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <Dashboard />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/patients" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <Patients />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/clients" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ClientsHubPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/checkin-admin" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <CheckInAdminPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/waitlist" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <WaitlistPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/client-tasks" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ClientTasksPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/booking-grids" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <BookingGridsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/client-interests" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ClientInterestsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/client-tags" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ClientTagsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/price-table" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <PriceTablePage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/service-rooms" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ServiceRoomsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/certificate-templates" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <CertificateTemplatesPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/contract-templates" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ContractTemplatesPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/suppliers" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <SuppliersPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/cities" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <CitiesPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/holidays" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <HolidaysPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/clinic-convenios" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ClinicConveniosPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/payment-methods" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <PaymentMethodsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/cost-centers" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <CostCentersPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/evolution-patterns" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <EvolutionPatternsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/devices" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <DevicesPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/exercises" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ExercisesPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/muscle-groups" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <MuscleGroupsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/assessment-forms" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <AssessmentFormsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/assessment-forms/:id" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <AssessmentFormEditorPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/clickable-records" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ClickableRecordsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/clickable-records/:id" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ClickableRecordEditorPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/laboratory" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="laboratory">
                        <LaboratoryPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route
                  path="/laboratory/orders/:id"
                  element={
                    <ProtectedRoute>
                      <LabDeepLinkRedirect kind="orders" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/laboratory/results/:id"
                  element={
                    <ProtectedRoute>
                      <LabDeepLinkRedirect kind="results" />
                    </ProtectedRoute>
                  }
                />
                
                <Route path="/patients/new" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <Patients autoOpenNewPatient={true} />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/patients/chart" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <PatientChartHubPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                
                <Route path="/pharmacy" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="pharmacy">
                        <PharmacyPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/injectables/plans" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="injectables">
                        <InjectableTreatmentPlansPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/injectables/session" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="injectables">
                        <InjectableSessionPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/injectables/report/:sessionId" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="injectables">
                        <InjectableClinicalReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                {/* ── Fase 5: Portal público do paciente ──────────────── */}
                <Route path="/injectable-portal/:clinicId/:patientId" element={
                  <Suspense fallback={<div />}>
                    <InjectablePatientPortalPage />
                  </Suspense>
                } />

                <Route path="/injectables" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="injectables">
                        <InjectablesHubPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/patients/:id/dashboard" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <PatientDashboardPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/appointments" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <Appointments />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/minha-agenda" element={<Navigate to="/appointments" replace />} />

                <Route path="/sales" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <SalesHubPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/products" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ProductsHubPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/clinical-flow" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ClinicalFlowPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/wellhub" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <WellhubInboxPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                {/* TotalPass saiu do produto: links antigos voltam ao painel */}
                <Route path="/totalpass" element={<Navigate to="/dashboard" replace />} />

                <Route path="/convenios" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="tiss">
                        <ConveniosPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/professionals" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <Professionals />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/professionals/home-care" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="homecare">
                        <ProfessionalsHomeCarePage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/inventory" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <Inventory />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <NewFinancialPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/transactions" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <TransactionsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/accounts" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <AccountsReceivablePage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/payments" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <PaymentsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/financial/settle-receipts" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <SettleReceiptsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/financial/receipts" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ReceiptIssuancePage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/financial/commissions" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <CommissionPaymentPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/financial/general-sales" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <GeneralSalesPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/financial/cash-flow" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <CashFlowPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/financial/dre" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <DREManagerialPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/financial/statement" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <FinancialStatementPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/financial/daily-cash" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <DailyCashPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/financial/fixed-revenue-simulator" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <FixedRevenueSimulatorPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/reports" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <FinancialReportsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/reconciliation" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ReconciliationPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/operational" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <OperationalDashboardPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/analytics" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <AnalyticsDashboardPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/billing" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="hasRecurringBilling">
                        <BillingDashboard />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/billing/plans" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="hasRecurringBilling">
                        <BillingPlansPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/billing/subscriptions" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="hasRecurringBilling">
                        <BillingSubscriptionsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/billing/analytics" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="hasRecurringBilling">
                        <BillingAnalyticsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/billing/alerts" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="hasRecurringBilling">
                        <BillingAlertsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/billing/monitoring" element={<Navigate to="/financial/billing" replace />} />
                
                <Route path="/financial/billing/trends" element={<Navigate to="/financial/billing" replace />} />
                
                <Route path="/financial/billing/risk" element={<Navigate to="/financial/billing" replace />} />
                
                <Route path="/financial/billing/automation" element={<Navigate to="/financial/billing" replace />} />
                
                <Route path="/financial/billing/notifications/templates" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="hasRecurringBilling">
                        <NotificationTemplatesPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/billing/stripe-connect/success" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="hasRecurringBilling">
                        <StripeConnectSuccessPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/billing/stripe-connect/error" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard feature="hasRecurringBilling">
                        <StripeConnectErrorPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/financial/governance" element={<Navigate to="/financial/billing" replace />} />
                
                <Route path="/financial/governance/audit" element={<Navigate to="/financial/billing" replace />} />
                
                <Route path="/reports" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ReportsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/reports/birthdays" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <BirthdaysReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/relatorio/aniversariante" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <BirthdaysReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/reports/clients" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ClientsReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/relatorio/cliente" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ClientsReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/reports/managerial" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ManagerialAnalysisPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/relatorio/analises-gerenciais" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ManagerialAnalysisPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/reports/appointments" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <AppointmentsReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/relatorio/atendimento" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <AppointmentsReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/reports/professional-commissioning" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ProfessionalCommissioningReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/relatorio/atendimento-profissional" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ProfessionalCommissioningReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/reports/product-sales" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ProductSalesReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/relatorio/vendas" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ProductSalesReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/reports/contracts" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ContractsReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/relatorio/contratos" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ContractsReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/reports/financial" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <FinancialReportsHubPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/reports/financial/commissions" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <CommissionReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/reports/financial/billing-history" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <BillingHistoryReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/reports/financial/receivables" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ReceivablesReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/reports/financial/payables" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <PayablesReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/reports/financial/cost-centers" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <CostCentersReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/reports/financial/receipts" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ReceiptsReportPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                
    
    
    {/* Redirecionamentos para manter compatibilidade */}
    <Route path="/integrations" element={<Navigate to="/settings?tab=4" replace />} />
    <Route path="/clinic-settings" element={<Navigate to="/client-center" replace />} />
    
    <Route path="/auth/google/callback" element={<GoogleCalendarCallback />} />
                
                
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <SettingsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/client-center" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ClientCenterPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/system-definitions" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <SystemDefinitionsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/definicoes" element={<Navigate to="/system-definitions" replace />} />

                <Route path="/system-users" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <SystemUsersPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/user" element={<Navigate to="/system-users" replace />} />
                <Route path="/system-users/create" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <SystemUserFormPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/system-users/edit/:id" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <SystemUserFormPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/user/create" element={<Navigate to="/system-users/create" replace />} />

                <Route path="/permissions" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <PermissionRolesPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/permissao" element={<Navigate to="/permissions" replace />} />
                <Route path="/permissions/edit/:id" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <PermissionRoleEditPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/permissao/edit/:id" element={<PermissionEditAlias />} />

                <Route path="/online-booking" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <OnlineBookingSalesPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/configuracao-agendamento-remoto" element={<Navigate to="/online-booking" replace />} />

                <Route path="/desktop-installers" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <DesktopInstallersPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/telemedicine" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <TelemedicinePage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/therapeutic-attendance" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <TherapeuticAttendancePage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/reminders" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <RemindersPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/ehr/:id" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <DynamicEHRPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/patient-ai/:id" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <PatientAIPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/patient/:id/ehr" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <PatientEHRPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/patient/:id/dynamic-ehr" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <DynamicEHRPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/patient/:id/ai-analysis" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <PatientAIPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                <Route path="/user-management" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <SoftRedirectUserManagement />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/user-management/legacy" element={<Navigate to="/system-users" replace />} />
                
                {/* Antiga página de propaganda com receita fixa em zero — o conteúdo real está em Análises Gerenciais */}
                <Route path="/advanced-reports" element={<Navigate to="/reports/managerial" replace />} />
                
                <Route path="/conversa-ia" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <IntegratedMedicalAIPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/analise-clinica" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <AIPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                {/* Tabela antiga do Stripe (planos 2025) — o catálogo atual fica em /checkout. */}
                <Route path="/pricing" element={<Navigate to="/checkout" replace />} />
                

                <Route path="/analysis/before-after" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <BeforeAfterAnalysisPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />

                <Route path="/clinical-tools" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <ClinicalToolsPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                {/* Rotas do Sistema Omnichannel (simplificado) */}
                <Route path="/omnichannel" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/channels" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <OmnichannelChannels />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/omnichannel/messages" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <AutomaticMessagesPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/omnichannel/automation" element={<OmnichannelRouteRedirect to="/omnichannel/messages" />} />
                <Route path="/omnichannel/contacts" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/marketing" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/integration" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/consent" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/reports" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <OmnichannelSoftLandingPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/omnichannel/chatbots" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/chatbots/test" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/chatbots/templates" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/chatbots/list" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/validation" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/analytics" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <OmnichannelSoftLandingPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                <Route path="/omnichannel/ab-testing" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/versions" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/journeys" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/segments" element={<OmnichannelRouteRedirect />} />
                <Route path="/omnichannel/inbox" element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SubscriptionGuard>
                        <SecretariaInboxPage />
                      </SubscriptionGuard>
                    </OnboardingGuard>
                  </ProtectedRoute>
                } />
                
                {/* Rota secreta - Monitoramento de Usuários (Admin Only) - DEVE VIR ANTES DA ROTA CATCH-ALL */}
                <Route path="/admin-usage-analytics" element={
                  <ProtectedRoute adminOnly={true}>
                    <AdminUsageAnalyticsPage />
                  </ProtectedRoute>
                } />

                {/* Rota de migração do Estoque Unificado — Admin Only */}
                <Route path="/admin-migrate-inventory" element={
                  <ProtectedRoute adminOnly={true}>
                    <MigrateInventoryPage />
                  </ProtectedRoute>
                } />
                
                {/* CRM legado (rotas removidas) — evita conteúdo stale / catch-all silencioso */}
                <Route path="/crm/*" element={<CrmLegacyRedirect />} />

                {/* Rota padrão — preserva deep link para pós-login */}
                <Route path="*" element={<CatchAllRedirect />} />
          </Routes>
          
          {/* Componente global de alertas críticos */}
          <Suspense fallback={null}>
            <CriticalFindingsAlert />
          </Suspense>
        </Suspense>
      </Box>
  );
};

const AuthGate: React.FC = () => {
  const { loading } = useAuthContext();
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ backgroundColor: 'background.default' }}
      >
        <CircularProgress 
          size={60} 
          thickness={4}
          sx={{ color: 'primary.main' }}
        />
      </Box>
    );
  }
  return <AppContent />;
};

const App: React.FC = () => {
  return (
    <ThemeContextProvider>
      <AuthProvider>
        <AuthGate />
      </AuthProvider>
    </ThemeContextProvider>
  );
};

export default App;
